
import { useState, useEffect, useCallback } from "react"
import {
    getInfoDataByAlmacenId,
    getCheckingDataByAlmacenId,
} from "../services/userService";
import { getPointsDataByAlmacenId, postLike } from "../services/transactionService";
import { sendEvaluation, getQuestions } from "../services/evaluationService";

export const useVoting = () => {
    const [almacenInfo, setAlmacenVotingInfo] = useState(null);
    const [almacenPoints, setAlmacenVotingPoints] = useState(null);
    const [loader, setLoader] = useState(false);
    const [error, setError] = useState(null);
    const [pageThanks, setPageThanks] = useState(false);
    const [clientId, setClientId] = useState(null);
    const [almacenId, setAlmacenId] = useState(null);
    const [evaluationId, setEvaluationId] = useState(null);
    const [customQuestions, setCustomQuestions] = useState([]);

    useEffect(() => {
        setClientId(genClientId())
        setEvaluationId(getUUID())
    }, [])

    const getQuestionByCountry = useCallback(async (country) => {
        if (!country) return [];
        try {
            const res = await getQuestions(country);
            setCustomQuestions(Array.isArray(res) ? res : []);
            return res;
        } catch (error) {
            console.error("Error obteniendo preguntas personalizadas:", error);
            setCustomQuestions([]);
            return [];
        }
    }, []);

    const getAlmacenInfo = useCallback(async (id) => {
        if (!id) {
            throw new Error("ID de almacén es requerido");
        }
        try {
            const res = await getInfoDataByAlmacenId(id);
            if (res) {
                setAlmacenVotingInfo(res);
                getQuestionByCountry(res?.country?.toLowerCase().trim());
            }
            return res;
        } catch (error) {
            console.error("Error obteniendo información del almacén:", error);
            setError("Error al cargar información del almacén");
            throw error;
        }
    }, [getQuestionByCountry]);

    const getAlmacenPoints = useCallback(async (id) => {
        if (!id) {
            throw new Error("ID de almacén es requerido");
        }
        try {
            const res = await getPointsDataByAlmacenId(id);
            setAlmacenVotingPoints(res);
            return res;
        } catch (error) {
            console.error("Error obteniendo puntos del almacén:", error);
            // No lanzar error, solo loguear - los puntos son opcionales
            setAlmacenVotingPoints(null);
            return null;
        }
    }, []);

    const getAlmacenAvailability = useCallback(async (id) => {
        if (!id) {
            throw new Error("ID de almacén es requerido");
        }
        try {
            return await getCheckingDataByAlmacenId(id);
        } catch (error) {
            console.error("Error obteniendo disponibilidad del almacén:", error);
            return null;
        }
    }, []);

    const mergeAlmacenCheckinData = useCallback((info, availability) => {
        const resto = availability?.restoId;
        if (!resto) return info;

        return {
            ...info,
            restoId: resto,
            restoName: resto?.name || info?.restoName || info?.name,
            address: resto?.address || info?.address,
            country: resto?.country || info?.country,
            place_id: resto?.place_id || info?.place_id,
            brand: resto?.brand || info?.brand,
            checkin: availability?.checkin,
            shiftConfig: availability?.shiftConfig,
        };
    }, []);

    const getDataAlmacen = useCallback(async (id) => {
        if (!id) {
            setError("ID de almacén no proporcionado");
            return;
        }

        try {
            setLoader(true);
            setError(null);
            setAlmacenId(id);

            // Usar Promise.all para llamadas paralelas
            const [almacenInfoData, almacenPointsData, almacenAvailabilityData] = await Promise.allSettled([
                getAlmacenInfo(id),
                getAlmacenPoints(id),
                getAlmacenAvailability(id)
            ]);

            // Manejar resultados
            if (almacenInfoData.status === 'rejected') {
                throw new Error("No se pudo cargar la información del almacén");
            }

            if (almacenInfoData.status === 'fulfilled') {
                const availability =
                    almacenAvailabilityData.status === 'fulfilled'
                        ? almacenAvailabilityData.value
                        : null;
                const mergedInfo = mergeAlmacenCheckinData(almacenInfoData.value, availability);
                setAlmacenVotingInfo(mergedInfo);
                getQuestionByCountry(mergedInfo?.country?.toLowerCase().trim());
            }

            // Los puntos son opcionales, no lanzamos error si fallan
            if (almacenPointsData.status === 'fulfilled') {
                setAlmacenVotingPoints(almacenPointsData.value);
            }

        } catch (error) {
            console.error("Error cargando datos del almacén:", error);
            setError(error.message || "Error al cargar datos del almacén");
        } finally {
            setLoader(false);
        }
    }, [getAlmacenInfo, getAlmacenPoints, getAlmacenAvailability, mergeAlmacenCheckinData, getQuestionByCountry]);

    const onLike = useCallback(async (id = null) => {
        const targetAlmacenId = id || almacenId;

        if (!targetAlmacenId) {
            const errorMsg = "ID de almacén no disponible";
            setError(errorMsg);
            return { success: false, error: errorMsg };
        }

        if (!clientId) {
            const errorMsg = "ID de cliente no disponible";
            setError(errorMsg);
            return { success: false, error: errorMsg };
        }

        try {
            const res = await postLike(targetAlmacenId, clientId);
            if (res?.msg === "like-timestamp") {
                const errorMsg = "Solo puedes votar una vez cada 24 horas.";
                setError(errorMsg);
                alert(errorMsg); // Fallback rápido si no hay UI de error dedicada
                return { success: false, error: errorMsg };
            }

            setPageThanks(res?.success || false);
            return res;
        } catch (error) {
            console.error("Error dando like:", error);
            setError("Error al procesar tu voto. Por favor, intenta nuevamente.");
            return { success: false, error: error.message };
        }
    }, [almacenId, clientId]);

    const genClientId = useCallback(() => {
        let clientId = window.localStorage.getItem("_hc");
        if (clientId) return clientId;

        clientId = getUUID();
        window.localStorage.setItem("_hc", clientId);
        return clientId;
    }, []);

    const getUUID = useCallback(() => {
        const array = new Uint8Array(16);
        window.crypto.getRandomValues(array);
        return Array.from(array, byte => byte.toString(16).padStart(2, "0")).join("");
    }, []);

    const onSelection = useCallback(async (evaluation) => {
        if (!evaluation) {
            setError("Datos de evaluación no proporcionados");
            return { success: false, error: "Datos de evaluación no proporcionados" };
        }

        if (!almacenId) {
            setError("ID de almacén no disponible");
            return { success: false, error: "ID de almacén no disponible" };
        }

        if (!clientId) {
            setError("ID de cliente no disponible");
            return { success: false, error: "ID de cliente no disponible" };
        }

        try {
            const restoId = String(
                almacenInfo?.restoId?._id ||
                almacenInfo?.restoId?.id ||
                almacenInfo?.restoId ||
                almacenInfo?._id ||
                ""
            );

            if (!restoId) {
                setError("ID de almacén no disponible");
                return { success: false, error: "ID de almacén no disponible" };
            }

            const data = { clientId, ...evaluation, evaluationId, restoId };
            const res = await sendEvaluation(data);
            return res;
        } catch (error) {
            console.error("Error enviando evaluación:", error);
            setError("Error al enviar tu evaluación. Por favor, intenta nuevamente.");
            return { success: false, error: error.message };
        }
    }, [almacenId, almacenInfo, clientId, evaluationId]);

    return {
        almacenInfo,
        almacenPoints,
        loader,
        error,
        pageThanks,
        clientId,
        customQuestions,
        getDataAlmacen,
        onLike,
        genClientId,
        onSelection
    }
}
